import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { List, X, SignOut, Bell } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export const DashboardLayout = ({ children, nav, title }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  // Phase F: hide admin areas a sub-admin role cannot access (SUPER sees all)
  const AREA = {
    "anav-properties": "operations", "anav-users": "operations", "anav-bookings": "operations",
    "anav-payments": "finance", "anav-finance": "finance", "anav-payouts": "finance",
    "anav-refunds": "finance", "anav-reconciliation": "finance", "anav-operations": "operations",
    "anav-disputes": "operations", "anav-support": "support", "anav-staff": "staff",
    "anav-leads": "support", "anav-owner-leads": "operations", "anav-coupons": "finance",
    "anav-marketing": "operations", "anav-marketplace": "operations", "anav-campaigns": "operations",
    "anav-whatsapp": "support", "anav-referrals": "operations", "anav-blog": "operations",
    "anav-seo": "operations", "anav-launch": "staff", "anav-settings": "staff",
  };
  const PERMS = { FINANCE: ["finance"], OPERATIONS: ["operations"], SUPPORT: ["support"] };
  const visibleNav = nav.filter((item) => {
    if (!item.testid || !item.testid.startsWith("anav-")) return true;
    const ar = user?.admin_role;
    if (!ar || ar === "SUPER") return true;
    if (item.testid === "anav-dashboard") return true;
    return (PERMS[ar] || []).includes(AREA[item.testid]);
  });

  useEffect(() => {
    api.get("/notifications/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
  }, [location.pathname]);

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      <div className="border-b border-stone-200 px-6 py-5">
        <p className="font-heading text-xl font-extrabold tracking-tight text-primary">Avinya <span className="font-semibold text-stone-800">Bookings</span></p>
        <p className="mt-1 text-[9px] font-semibold uppercase tracking-[0.28em] text-amber-600">Book · Relax · Celebrate</p>
        <p className="mt-2 text-xs tracking-[0.2em] text-stone-400 uppercase">{title} Panel</p>
      </div>
      <nav className="flex-1 space-y-1 px-3 py-4">
        {visibleNav.map((item) => {
          const active = location.pathname === item.to;
          const Icon = item.icon;
          return (
            <button
              key={item.to}
              data-testid={item.testid}
              onClick={() => {
                navigate(item.to);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                active ? "bg-primary text-white" : "text-stone-600 hover:bg-accent"
              }`}
            >
              <Icon size={20} weight={active ? "fill" : "regular"} />
              {item.label}
            </button>
          );
        })}
      </nav>
      <div className="border-t border-stone-200 px-4 py-4">
        <button data-testid="dash-notif-btn" onClick={() => { navigate("/notifications"); setOpen(false); }} className="mb-3 flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-stone-600 transition-colors hover:bg-accent">
          <span className="relative"><Bell size={20} />{unread > 0 && <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">{unread}</span>}</span>
          Notifications
        </button>
        <div className="mb-3 px-2">
          <p className="text-sm font-semibold text-stone-800 line-clamp-1">{user?.name}</p>
          <p className="text-xs text-stone-400 line-clamp-1">{user?.email}</p>
        </div>
        <Button
          data-testid="logout-btn"
          variant="outline"
          className="w-full justify-start gap-2 rounded-xl"
          onClick={() => {
            logout();
            navigate("/login");
          }}
        >
          <SignOut size={18} /> Logout
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="hidden w-64 shrink-0 border-r border-stone-200 bg-white lg:block">
        <SidebarContent />
      </aside>

      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 bg-white">
            <SidebarContent />
          </aside>
        </div>
      )}

      <div className="flex flex-1 flex-col">
        <header className="sticky top-0 z-40 flex items-center justify-between border-b border-stone-200 bg-white/80 px-4 py-3 backdrop-blur-xl lg:hidden">
          <button data-testid="sidebar-toggle" onClick={() => setOpen(true)} className="rounded-lg p-2 hover:bg-accent">
            {open ? <X size={22} /> : <List size={22} />}
          </button>
          <span className="font-heading font-bold text-primary">Avinya {title}</span>
          <button data-testid="dash-notif-btn-mobile" onClick={() => navigate("/notifications")} className="relative rounded-lg p-2 hover:bg-accent">
            <Bell size={20} />
            {unread > 0 && <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">{unread}</span>}
          </button>
        </header>
        <main className="flex-1 p-4 md:p-8">{children}</main>
      </div>
    </div>
  );
};
